import React, { ChangeEvent, ChangeEventHandler, FC, FormEventHandler, useEffect, useState } from 'react'
import Logo from '../images/logo-dark.svg'
import BottomVector from '../images/payment-success-vector.svg'
import Page from '../components/Page'
import { ContactInformation, isValidationFailedResponse, Transfer, TransferDirection } from '../types'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import Notice from '../components/Notice'
import Icons from '../components/Icons'
import useOrders, { OrderShowResponse } from '../hooks/useOrders'
import SummaryRow from '../components/SummaryRow'
import { formatAirport } from '../transforms/airport'
import { format as formatDate, parseISO } from 'date-fns'
import { Config, PageURLs } from '../common'
import ListGroup from '../components/ListGroup'
import _ from 'lodash'
import useTransfers, { TransferUpdateResponse } from '../hooks/useTransfers'
import { AxiosResponse } from 'axios'
import { useHistory } from 'react-router-dom'
import PaymentTick from '../images/payment-success.svg'
import Overlay from '../components/Overlay'
import { logError } from '../store/slices/app'

type EditableTransferRowProps = {
  label: string
  value?: string
  error?: string
  isLoading: boolean
  onChange?: ChangeEventHandler<HTMLInputElement>
}
const EditableTransferRow: FC<EditableTransferRowProps> = ({
  isLoading,
  label,
  value: initialValue = '',
  onChange,
  error,
  children,
}) => {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    // setValue(initialValue)
  }, [initialValue])

  return (
    <div className="summary-row summary-row--large d-flex pb-2 pl-2 ">
      <span className="summary-row-left placeholder-text">{label}</span>
      <div className="summary-row-right w-100">
        <div className="input-group">
          {children ? (
            children
          ) : (
            <input
              type="text"
              className="form-control"
              disabled={isLoading}
              placeholder={label}
              value={value}
              onChange={(event) => {
                setValue(event.currentTarget.value)
                onChange?.(event)
              }}
            />
          )}
        </div>
        {error && <span className="d-block invalid-feedback flex-grow-1 mr-1">{error}</span>}
      </div>
    </div>
  )
}

const BookingReview = () => {
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(true)

  const [validationErrors, setValidationErrors] = useState({})
  const [payload, setPayload] = useState<Record<string, Required<ContactInformation>>>({})
  const [initialPayload, setInitialPayload] = useState<Record<string, Required<ContactInformation>>>({})
  const [order, setOrder] = useState<OrderShowResponse | null>(null)
  const api = useOrders()
  const transferApi = useTransfers()
  const dispatch = useDispatch()
  const { lastOrderId } = useSelector((state: RootState) => state.app)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(true)

  useEffect(() => {
    if (!lastOrderId) {
      history.push(PageURLs.Home)
    }
  }, [])

  const mappedFormPayload = (transfers: Transfer[]) => {
    return transfers.reduce((payload, transfer) => {
      const { contact_first_name, contact_last_name, contact_email, contact_phone } = transfer

      return {
        ...payload,
        [transfer.booking_id]: {
          contact_first_name,
          contact_last_name,
          contact_email,
          contact_phone,
        },
      }
    }, {})
  }

  useEffect(() => {
    async function boot() {
      try {
        const { data } = await api.find(lastOrderId)
        setOrder(data)
        setPayload(mappedFormPayload(data.transfers))
        setInitialPayload(mappedFormPayload(data.transfers))
        setIsLoading(false)
      } catch (err) {
        dispatch(logError(err))
        history.push(PageURLs.Home)
      }
    }

    boot()
  }, [])

  const onChangeEventHandlerFactory = (bookingId: string, attribute: string): ChangeEventHandler => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setPayload({
        ...payload,
        [bookingId]: {
          ...payload[bookingId],
          [attribute]: event.currentTarget.value,
        },
      })
    }
  }

  const handleBookingUpdate: FormEventHandler = async (e) => {
    e.preventDefault()
    setValidationErrors({})
    setIsLoading(true)

    const changed = Object.keys(payload).filter(
      (bookingId) => !_.isEqual(initialPayload[bookingId], payload[bookingId])
    )

    const requests = []
    for (const bookingId of changed) {
      requests.push(transferApi.update(bookingId, payload[bookingId]))
    }

    const responses = await Promise.allSettled(requests)
    let errors = {}
    responses.forEach((promise) => {
      if (promise.status === 'rejected' && isValidationFailedResponse(promise.reason)) {
        const error = promise.reason
        const bookingId = error.config!.url!.split('/').pop() as string

        errors = {
          ...errors,
          [bookingId]: {
            ...error.response.data.data.params,
          },
        }
      }

      if (promise.status === 'fulfilled') {
        const { data } = promise.value as AxiosResponse<TransferUpdateResponse>
        const transfer = data.transfer as Required<Transfer>

        setInitialPayload({
          ...initialPayload,
          [transfer.booking_id]: {
            contact_first_name: transfer.contact_first_name,
            contact_last_name: transfer.contact_last_name,
            contact_email: transfer.contact_email,
            contact_phone: transfer.contact_phone,
          },
        })
      }
    })

    if (_.isEmpty(errors)) {
      history.push(PageURLs.Transfers)
    } else {
      setValidationErrors(errors)
      // show errors...
      setIsLoading(false)
    }
  }

  return (
    <>
      <Page className="bg-white">
        <div className="position-relative">
          <div className="container">
            <div className="row pt-4 pt-lg-5">
              <div className="col">
                <a href="/">
                  <img src={Logo} alt="Logo" />
                </a>
              </div>
            </div>

            <div className="row justify-content-center align-items-center">
              <div className="col-12 col-lg-8">
                <div className="my-5 my-lg-6 pb-6 pb-lg-0">
                  <h3>Your booking</h3>

                  <Notice variant="danger">
                    <span className="d-flex align-items-center">
                      <Icons.Exclamation className="flex-shrink-0" />
                      <span className="ml-2">
                        All transfers are on your name and phone by default. Edit if necessary. You can also update
                        these later.
                      </span>
                    </span>
                  </Notice>

                  <form onSubmit={handleBookingUpdate}>
                    {order?.transfers.map((transfer) => {
                      const { direction, pickup_datetime } = transfer
                      const address = transfer.address_formatted
                      const airport = formatAirport(transfer)
                      const [origin, destination] =
                        direction === TransferDirection.ToAirport ? [address, airport] : [airport, address]
                      const formattedDate = formatDate(parseISO(pickup_datetime), Config.DateFormat)
                      const formattedTime = formatDate(parseISO(pickup_datetime), Config.TimeFormat)
                      const transferValidationErrors: Record<string, string> | undefined =
                        validationErrors[transfer.booking_id]

                      return (
                        <ListGroup.Items key={transfer.booking_id} className="mb-3">
                          <ListGroup.Item>
                            <div className={`booking-review__item px-2 pt-2 px-lg-4 pt-lg-4 pb-3`}>
                              <SummaryRow label="From" text={origin} className="pt-2 summary-row--large" />
                              <SummaryRow label="To" text={destination} className="summary-row--large" />
                              <SummaryRow
                                label="Pick up"
                                text={`${formattedDate} at ${formattedTime}`}
                                className="summary-row--large"
                              />

                              <EditableTransferRow
                                label={'First name'}
                                isLoading={false}
                                onChange={onChangeEventHandlerFactory(transfer.booking_id, 'contact_first_name')}
                                value={transfer.contact_first_name}
                                error={transferValidationErrors?.['contact_first_name']}
                              />
                              <EditableTransferRow
                                label={'Last name'}
                                isLoading={false}
                                onChange={onChangeEventHandlerFactory(transfer.booking_id, 'contact_last_name')}
                                value={transfer.contact_last_name}
                                error={transferValidationErrors?.['contact_last_name']}
                              />
                              <EditableTransferRow
                                label={'Email'}
                                isLoading={false}
                                onChange={onChangeEventHandlerFactory(transfer.booking_id, 'contact_email')}
                                value={transfer.contact_email}
                                error={transferValidationErrors?.['contact_email']}
                              />
                              <EditableTransferRow
                                label={'Phone'}
                                isLoading={false}
                                onChange={onChangeEventHandlerFactory(transfer.booking_id, 'contact_phone')}
                                value={transfer.contact_phone}
                                error={transferValidationErrors?.['contact_phone']}
                              />
                              {/*<PhoneInput*/}
                              {/*  country={'gb'}*/}
                              {/*  value={phone}*/}
                              {/*  onChange={(phone) => false}*/}
                              {/*  inputClass="pl-5"*/}
                              {/*  inputProps={{*/}
                              {/*    required: true,*/}
                              {/*  }}*/}
                              {/*/>*/}
                              {/*</EditableTransferRow>*/}
                            </div>
                          </ListGroup.Item>
                        </ListGroup.Items>
                      )
                    })}

                    <button type="submit" className="btn btn-primary btn-block border-0 btn-lg">
                      {isLoading ? <div className="spinner-border text-light mr-2" role="status"></div> : null}
                      <span>Save changes</span>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="bottom-vector position-absolute" style={{ bottom: 0, right: 0 }}>
            <img src={BottomVector} alt="AirportCars" />
          </div>
        </div>
      </Page>
      <Overlay isVisible={showPaymentSuccess && isLoading === false}>
        <div className="payment-success-wrapper">
          <div className="payment-success">
            <div className="payment-success__title__wrapper">
              <img
                aria-hidden={true}
                src={PaymentTick}
                alt={'Payment success graphic'}
                className="payment-success__graphic"
              />
              <img src={Logo} alt="Logo" style={{ width: '96px' }} />
              <h1>Payment successful</h1>
            </div>

            <div>
              <Notice variant="info">
                <span className="d-flex align-items-center">
                  <Icons.Exclamation className="flex-shrink-0" />
                  <span className="ml-2">
                    Your transfers are still being processed. We&apos;ll send you an email once it’s complete
                  </span>
                </span>
              </Notice>
              <button className="btn btn-primary w-100" onClick={() => setShowPaymentSuccess(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      </Overlay>
    </>
  )
}

export default BookingReview
